import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { useQuery } from 'react-query';
import type { SxProps } from '@material-ui/system';
import { usePopover } from '../hooks/use-popover';
import { Selector as SelectorIcon } from '../icons/selector';
import { SUPER_ADMIN_TENANT, Tenant } from '../types/tenant';
import { useAxios } from '../hooks/use-axios';
import { useAuth } from '../hooks/use-auth';
import logger from '../utils/logger';
import { fetchAllPages } from '../utils/fetch-all-pages';

interface TenantPopoverProps {
  currentTenant: Tenant;
  onTenantChange: (tenantId: string) => void;
  tenants: Tenant[];
  sx?: SxProps;
}

export const TenantPopover: FC<TenantPopoverProps> = (props) => {
  const { currentTenant, tenants, onTenantChange, sx, ...other } = props;
  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const { token, refreshTenants } = useAuth();
  const { axios } = useAxios();
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Reset filter every time the popover opens, and focus the search input.
  useEffect(() => {
    if (open) {
      setSearch('');
      // Wait one tick for the popover to mount before focusing.
      const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const { data: userTenants } = useQuery(
    ['active-tenants', token],
    async () => {
      const allTenants = await fetchAllPages<Tenant>(axios, '/users/me/tenants');

      if (tenants.some((t) => t.id === SUPER_ADMIN_TENANT.id)) {
        return [SUPER_ADMIN_TENANT, ...allTenants];
      }

      return allTenants;
    },
    {
      onSuccess: (data) => {
        if (
          data?.length !== tenants?.length ||
          data?.find((t) => tenants?.find((t2) => t2.id === t.id && t2.updatedAt !== t.updatedAt))
        ) {
          logger('Tenants refreshed');
          refreshTenants();
        }
      },
      initialData: [],
    },
  );

  const handleTenantChange = (tenantId: string): void => {
    handleClose();
    onTenantChange?.(tenantId);
  };

  const hasSuperAdmin = useMemo(
    () => Boolean(userTenants?.some((t) => t.id === SUPER_ADMIN_TENANT.id)),
    [userTenants],
  );

  const filteredTenants = useMemo(() => {
    const all = (userTenants || []).filter((t) => t.id !== SUPER_ADMIN_TENANT.id);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((t) => (t.title || '').toLowerCase().includes(q));
  }, [userTenants, search]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && filteredTenants.length === 1) {
      handleTenantChange(filteredTenants[0].id);
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          borderRadius: 1,
          display: 'flex',
          p: 1,
          minWidth: 180,
          maxWidth: 300,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
          ...sx,
        }}
        title={currentTenant.title}
        {...other}
      >
        <Typography
          color="textSecondary"
          sx={{
            whiteSpace: 'nowrap',
            color: 'primary.contrastText',
            mr: 3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          variant="subtitle2"
        >
          {currentTenant.title}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <SelectorIcon fontSize="small" />
      </ButtonBase>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 320 },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            inputRef={searchInputRef}
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search tenants…"
            size="small"
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {hasSuperAdmin && (
          <>
            <List dense disablePadding>
              <ListItemButton
                selected={currentTenant.id === SUPER_ADMIN_TENANT.id}
                onClick={() => handleTenantChange(SUPER_ADMIN_TENANT.id)}
              >
                <ListItemText primary={SUPER_ADMIN_TENANT.title} />
              </ListItemButton>
            </List>
            <Divider />
          </>
        )}
        <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {filteredTenants.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="textSecondary">
                No matching tenants
              </Typography>
            </Box>
          ) : (
            filteredTenants.map((tenant) => (
              <ListItemButton
                key={tenant.id}
                selected={tenant.id === currentTenant.id}
                onClick={() => handleTenantChange(tenant.id)}
              >
                <ListItemText
                  primary={tenant.title}
                  primaryTypographyProps={{ noWrap: true, title: tenant.title }}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

TenantPopover.propTypes = {
  // @ts-ignore
  currentTenant: PropTypes.object.isRequired,
  onTenantChange: PropTypes.func,
  tenants: PropTypes.array.isRequired,
  sx: PropTypes.object,
};
